/// <amd-module name="ExtensionManagement/ManagementHub/ExtensionManagementHub" />
import React = require("react");

import { ObservableValue, ObservableArray } from "VSS/Core/Observable";

import { IPageStateHistoryService, HistoryBehavior } from "VSS/Features/History";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem, HeaderCommandBarWithFilter } from "VSSUI/HeaderCommandBar";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";

import { ExtensionRequestState } from "./Contracts/ExtensionManagement";
import { ExtensionManagementBreadcrumbService } from "./ExtensionManagementBreadcrumbService";
import { ExtensionsMasterDetailView } from "./ExtensionsMasterDetailView";
import { IExtensionsService } from "./ExtensionsService";
import { ExtensionsTable } from "./ExtensionsTable";
import { SecurityDialog } from "./SecurityDialog";
import { IExtensionViewModel, TabType } from "./UIContracts";

import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";

const enum View {
    MainView,
    MasterDetailView
}

const tabIdToTabName: { [key: string]: string } = {
    installed: Resources.Installed,
    requested: Resources.Requested,
    shared: Resources.Shared
};

const enumToId: { [key: number]: string } = {
    [ExtensionRequestState.Open]: "pending",
    [ExtensionRequestState.Accepted]: "approved",
    [ExtensionRequestState.Rejected]: "rejected"
};

const installedfilterMenuItems: IListBoxItem<{}>[] = [
    { id: "actionrequired", text: Resources.ActionRequired },
    { id: "disabled", text: Resources.Disabled }
];

const requestsfilterMenuItems: IListBoxItem<{}>[] = [
    { id: "pending", text: Resources.Pending },
    { id: "approved", text: Resources.Approved },
    { id: "rejected", text: Resources.Rejected }
];

class ExtensionsManagementHub extends VssComponent<IVssComponentProperties> {
    public pageContext: IVssPageContext;

    private commandBarItems: IHeaderCommandBarItem[];
    private createNewEntryOnNextUpdate: boolean;
    private currentExtensions: IExtensionViewModel[] | undefined;
    private currentView: ObservableValue<View>;
    private enteredInstalledFilterKeyword: ObservableValue<string>;
    private extensionManagementBreadcrumbService: ExtensionManagementBreadcrumbService;
    private extensionsService: IExtensionsService;
    private filterToggled: ObservableValue<boolean>;
    private installedFilter: Filter;
    private isSecurityDialogOpen: ObservableValue<boolean>;
    private pageStateHistoryService: IPageStateHistoryService;
    private requestedFilter: Filter;
    private selectedExtension: IExtensionViewModel | undefined;
    private selectedExtensionIdentifier: ObservableValue<string>;
    private selectedInstalledFilterIds: ObservableArray<string>;
    private selectedRequestFilterIds: ObservableArray<string>;
    private selectedTabId: ObservableValue<string>;
    private selectionInstalledTab: DropdownMultiSelection;
    private selectionRequestedTab: DropdownMultiSelection;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this.pageContext = context.pageContext;
        this.extensionsService = this.pageContext.getService<IExtensionsService>("IExtensionsService");
        this.pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this.extensionManagementBreadcrumbService = this.pageContext.getService<ExtensionManagementBreadcrumbService>(
            "extension-management-hub-breadcrumb-service"
        );

        this.selectedTabId = new ObservableValue(TabType.installed);
        this.pageStateHistoryService.registerObservable(this.selectedTabId, "tab", HistoryBehavior.newEntry);

        // check if value of selected tab is invalid
        // fix it if invalid
        if (!this.isValidTabName(this.selectedTabId.value)) {
            if (this.isValidTabName(this.selectedTabId.value.toLowerCase())) {
                this.selectedTabId.value = this.selectedTabId.value.toLowerCase();
            } else {
                this.selectedTabId.value = TabType.installed;
            }
        }

        this.installedFilter = new Filter();
        this.pageStateHistoryService.registerFilter(this.installedFilter, "installed-", [
            {
                dataType: "string",
                filterKey: "keyword",
                historyBehavior: HistoryBehavior.replace
            },
            {
                dataType: "object",
                filterKey: "state",
                historyBehavior: HistoryBehavior.replace,
                mappingFunction: (statuses: string[]): string => statuses.join(),
                parsingFunction: this.parseInstalledFiltersURLValue
            }
        ]);

        this.requestedFilter = new Filter();
        this.pageStateHistoryService.registerFilter(this.requestedFilter, "", [
            {
                dataType: "object",
                filterKey: "status",
                historyBehavior: HistoryBehavior.replace,
                mappingFunction: (statuses: string[]): string => statuses.join(),
                parsingFunction: this.parseRequestedFiltersURLValue
            }
        ]);

        // if we are on requested tab and no filter is selected, select pending
        const statuses = this.requestedFilter.getFilterItemValue<string[]>("status") || [];
        if (this.selectedTabId.value === TabType.requested && statuses.length === 0) {
            this.requestedFilter.setState({ status: { value: ["pending"] } });
        }

        this.enteredInstalledFilterKeyword = new ObservableValue<string>(this.installedFilter.getFilterItemValue<string>("keyword") || "");
        this.selectedInstalledFilterIds = new ObservableArray<string>(this.installedFilter.getFilterItemValue<string[]>("state") || []);
        this.selectedRequestFilterIds = new ObservableArray<string>(this.requestedFilter.getFilterItemValue<string[]>("status") || []);

        this.currentView = new ObservableValue(View.MainView);
        this.selectedExtensionIdentifier = new ObservableValue("");
        this.selectedExtensionIdentifier.subscribe(this.onSelectedExtensionIdentifierChanged);
        this.pageStateHistoryService.registerObservable(this.selectedExtensionIdentifier, "extension", this.getHistoryBehavior);

        if (this.selectedExtensionIdentifier.value !== "") {
            this.currentView.value = View.MasterDetailView;
        }

        this.commandBarItems = [
            {
                id: "security",
                text: Resources.Security,
                onActivate: this.showSercurityDialog
            },
            {
                id: "marketplace",
                text: Resources.BrowseMarketplace,
                href: this.extensionsService.getMarketPlaceUrl(),
                target: "_blank"
            }
        ];

        if (!this.extensionsService.getIsHosted()) {
            this.commandBarItems.push({
                id: "gallery",
                text: Resources.BrowseLocalExtensions,
                href: this.extensionsService.getGalleryWebUrl(),
                target: "_blank"
            });
        }

        this.isSecurityDialogOpen = new ObservableValue(false);
        this.filterToggled = new ObservableValue<boolean>(false);
        this.selectionInstalledTab = new DropdownMultiSelection();
        this.selectionRequestedTab = new DropdownMultiSelection();
    }

    public render(): JSX.Element {
        if (this.extensionsService.getErrorLoadingData()) {
            return (
                <div className="extension-management-hub flex-column flex-grow justify-center">
                    <ZeroData
                        imagePath={getAssetLocation(this.pageContext, "ms.vss-extmgmt-web/ext-management-hub/zerodata.png")}
                        imageAltText=""
                        primaryText={Resources.DataErrorMessage}
                    />
                </div>
            );
        }

        const tabs: JSX.Element[] = [];

        tabs.push(<Tab name={Resources.Installed} id={TabType.installed} key={TabType.installed} />);
        tabs.push(<Tab name={Resources.Requested} id={TabType.requested} key={TabType.requested} />);

        if (this.extensionsService.getIsHosted()) {
            tabs.push(<Tab name={Resources.Shared} id={TabType.shared} key={TabType.shared} />);
        }

        return (
            <Page className="extension-management-hub flex-grow">
                <Observer currentView={this.currentView}>
                    {(props: { currentView: View }) => {
                        return props.currentView === View.MainView ? (
                            <>
                                <Header title={Resources.Extensions} commandBarItems={this.commandBarItems} titleAriaLevel={2} />
                                <Observer selectedTabId={this.selectedTabId}>
                                    {(props: { selectedTabId: string }) => {
                                        return (
                                            <>
                                                <TabBar
                                                    onSelectedTabChanged={this.onSelectedTabChanged}
                                                    tabSize={TabSize.Tall}
                                                    renderAdditionalContent={
                                                        props.selectedTabId === TabType.installed
                                                            ? this.renderManageFilterBar
                                                            : props.selectedTabId === TabType.requested
                                                            ? this.renderRequestedFilterBar
                                                            : undefined
                                                    }
                                                    selectedTabId={props.selectedTabId}
                                                >
                                                    {tabs}
                                                </TabBar>
                                                {this.getTabContent(props.selectedTabId)}
                                            </>
                                        );
                                    }}
                                </Observer>
                                <Observer isDialogOpen={this.isSecurityDialogOpen}>
                                    {(props: { isDialogOpen: boolean }) => {
                                        return props.isDialogOpen ? (
                                            <SecurityDialog
                                                onDismiss={this.onDismissSercurityDialog}
                                                canUserManageSecurity={this.extensionsService.getCanUserManageSecurity()}
                                                userId={this.extensionsService.getUserId()}
                                                supportServicePrincipals={this.extensionsService.getSupportServicePrincipals()}
                                            />
                                        ) : null;
                                    }}
                                </Observer>
                            </>
                        ) : this.extensionsService.getIsLoadingRequestedExtensionViewModels().value &&
                          this.selectedTabId.value === TabType.requested ? (
                            <Observer loadingRequests={this.extensionsService.getIsLoadingRequestedExtensionViewModels()}>
                                {(props: { loadingRequests: boolean }) => {
                                    return props.loadingRequests ? <Spinner /> : this.getMasterDetailsView();
                                }}
                            </Observer>
                        ) : (
                            this.getMasterDetailsView()
                        );
                    }}
                </Observer>
            </Page>
        );
    }

    componentDidMount() {
        this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").markTimeToInteractive("Open ALM", "Extensions.AdminHub.Load");
        this.updateBreadCrumb(this.selectedTabId.value, "");

        this.installedFilter.subscribe(this.onInstalledFilterChanged, FILTER_CHANGE_EVENT);
        this.requestedFilter.subscribe(this.onRequestedFilterChanged, FILTER_CHANGE_EVENT);
        this.selectedTabId.subscribe(this.updateBreadCrumb);
    }

    componentWillUnmount() {
        this.installedFilter.unsubscribe(this.onInstalledFilterChanged, FILTER_CHANGE_EVENT);
        this.requestedFilter.unsubscribe(this.onRequestedFilterChanged, FILTER_CHANGE_EVENT);
        this.selectedTabId.unsubscribe(this.updateBreadCrumb);
        this.selectedExtensionIdentifier.unsubscribe(this.onSelectedExtensionIdentifierChanged);
    }

    private getFilteredExtensions = (): IExtensionViewModel[] => {
        if (this.selectedTabId.value === TabType.installed) {
            let filteredExtensions = this.extensionsService.getInstalledExtensionViewModels().value;

            if (this.enteredInstalledFilterKeyword.value !== "") {
                const lowercaseKeyword = this.enteredInstalledFilterKeyword.value.toLowerCase();
                filteredExtensions = filteredExtensions.filter((extension) => {
                    return (
                        extension.extensionName.toLowerCase().includes(lowercaseKeyword) ||
                        extension.publisherName.toLowerCase().includes(lowercaseKeyword)
                    );
                });
            }

            const showDisabled = this.selectedInstalledFilterIds.value.indexOf("disabled") !== -1;
            const showActionRequired = this.selectedInstalledFilterIds.value.indexOf("actionrequired") !== -1;

            if (showDisabled || showActionRequired) {
                filteredExtensions = filteredExtensions.filter((extension) => {
                    return (extension.reauthorizationRequired && showActionRequired) || (extension.extensionDisabled.value && showDisabled);
                });
            }

            // if none of the filters are selected show all extensions
            return filteredExtensions;
        } else if (this.selectedTabId.value === TabType.requested) {
            return this.extensionsService.getRequestedExtensionViewModels().value.filter((extension) => {
                return this.selectedRequestFilterIds.value.indexOf(enumToId[extension.requestState]) !== -1;
            });
        } else {
            return this.extensionsService.getSharedExtensionViewModels().value;
        }
    };

    private getHistoryBehavior = (newValue: string): HistoryBehavior => {
        if (this.createNewEntryOnNextUpdate) {
            this.createNewEntryOnNextUpdate = false;
            return HistoryBehavior.newEntry;
        }

        return HistoryBehavior.replace;
    };

    private getMasterDetailsView(): JSX.Element {
        return (
            <ExtensionsMasterDetailView
                extensions={this.currentExtensions ? this.currentExtensions : this.getFilteredExtensions()}
                extensionsService={this.extensionsService}
                initialSelection={
                    this.selectedExtension
                        ? this.selectedExtension
                        : this.selectedTabId.value === TabType.requested
                        ? this.extensionsService.getRequestedExtensionByIdentifier(this.selectedExtensionIdentifier.value)
                        : this.extensionsService.getExtensionByIdentifier(this.selectedExtensionIdentifier.value)
                }
                pageContext={this.pageContext}
                selectedExtensionIdentifier={this.selectedExtensionIdentifier}
                selectedTabId={this.selectedTabId.value}
                switchToMainView={this.switchToMainView}
            />
        );
    }

    private getTabContent(selectedTabId: string): JSX.Element | undefined {
        if (selectedTabId === TabType.installed) {
            return (
                <>
                    <Observer filterToggled={this.filterToggled}>
                        {(props: { filterToggled: boolean }) => {
                            return (
                                props.filterToggled && (
                                    <div className="page-content-left page-content-right page-content-top">
                                        <FilterBar filter={this.installedFilter}>
                                            <KeywordFilterBarItem filterItemKey="keyword" />
                                            <DropdownFilterBarItem
                                                filterItemKey="state"
                                                hideClearAction={true}
                                                items={installedfilterMenuItems}
                                                placeholder={Resources.State}
                                                selection={this.selectionInstalledTab}
                                            />
                                        </FilterBar>
                                    </div>
                                )
                            );
                        }}
                    </Observer>
                    <Observer
                        extensions={this.extensionsService.getInstalledExtensionViewModels()}
                        filters={this.selectedInstalledFilterIds}
                        keyword={this.enteredInstalledFilterKeyword}
                    >
                        {() => {
                            return (
                                <ExtensionsTable
                                    extensions={this.getFilteredExtensions()}
                                    OnExtensionSelection={this.switchToMasterDetailView}
                                    pageContext={this.pageContext}
                                    tableId={TabType.installed}
                                />
                            );
                        }}
                    </Observer>
                </>
            );
        } else if (selectedTabId === TabType.requested) {
            return (
                <Observer
                    filters={this.selectedRequestFilterIds}
                    loadingRequests={this.extensionsService.getIsLoadingRequestedExtensionViewModels()}
                    requests={this.extensionsService.getRequestedExtensionViewModels()}
                >
                    {(props: { loadingRequests: boolean }) => {
                        return props.loadingRequests ? (
                            <Spinner />
                        ) : (
                            <ExtensionsTable
                                extensions={this.getFilteredExtensions()}
                                OnExtensionSelection={this.switchToMasterDetailView}
                                pageContext={this.pageContext}
                                tableId={TabType.requested}
                            />
                        );
                    }}
                </Observer>
            );
        } else if (selectedTabId === TabType.shared) {
            return (
                <ExtensionsTable
                    extensions={this.getFilteredExtensions()}
                    OnExtensionSelection={this.switchToMasterDetailView}
                    pageContext={this.pageContext}
                    tableId={TabType.shared}
                />
            );
        } else {
            return undefined;
        }
    }

    private isValidTabName(tabName: string): boolean {
        return tabName === TabType.installed || tabName === TabType.requested || tabName === TabType.shared;
    }

    private onDismissSercurityDialog = (): void => {
        this.isSecurityDialogOpen.value = false;
    };

    private onInstalledFilterChanged = (): void => {
        this.selectedInstalledFilterIds.value = this.installedFilter.getFilterItemValue<string[]>("state") || [];
        this.enteredInstalledFilterKeyword.value = this.installedFilter.getFilterItemValue<string>("keyword") || "";
    };

    private onRequestedFilterChanged = (): void => {
        this.selectedRequestFilterIds.value = this.requestedFilter.getFilterItemValue<string[]>("status") || [];
    };

    private onSelectedTabChanged = (newTabId: string) => {
        const previousTabId = this.selectedTabId.value;
        this.selectedTabId.value = newTabId;

        if (previousTabId === TabType.installed) {
            this.installedFilter.reset();
        } else if (previousTabId === TabType.requested) {
            this.requestedFilter.reset();
        }

        if (newTabId === TabType.requested) {
            this.requestedFilter.setState({ status: { value: ["pending"] } });
        }
    };

    // This will get called on view switch from UI and when browser navigation is used
    private onSelectedExtensionIdentifierChanged = (value: string, action: string): void => {
        // if selectedExtensionIdentifier's new value is not consistent with the value of view
        // the view should be changed, this will happen on browser navigation
        if (!value && this.currentView.value !== View.MainView) {
            // when value is empty string then view should be main view
            this.currentView.value = View.MainView;
            // reset the master details data
            // so next time it will pull new data than using stale data
            this.selectedExtension = undefined;
            this.currentExtensions = undefined;
        } else if (value && this.currentView.value !== View.MasterDetailView) {
            // when value is non empty string then view should be master details view
            this.currentView.value = View.MasterDetailView;
        }
    };

    private parseInstalledFiltersURLValue = (states: string): string[] => {
        const statesArray: string[] = states.toLowerCase().split(",");
        const validatedArray: string[] = [];
        statesArray.forEach((state) => {
            let valid = false;

            // check if state in filter is a valid state
            installedfilterMenuItems.forEach((validState) => {
                if (validState.id === state) {
                    valid = true;
                }
            });

            if (valid) {
                validatedArray.push(state);
            }
        });

        return validatedArray;
    };

    private parseRequestedFiltersURLValue = (statuses: string): string[] => {
        const statusArray: string[] = statuses.toLowerCase().split(",");
        const validatedArray: string[] = [];
        statusArray.forEach((status) => {
            let valid = false;

            // check if status in filter is a valid status
            requestsfilterMenuItems.forEach((validStatus) => {
                if (validStatus.id === status) {
                    valid = true;
                }
            });

            if (valid) {
                validatedArray.push(status);
            }
        });

        return validatedArray;
    };

    private renderManageFilterBar = () => {
        return <HeaderCommandBarWithFilter filter={this.installedFilter} filterToggled={this.filterToggled} items={[]} />;
    };

    private renderRequestedFilterBar = (): JSX.Element => {
        return (
            <DropdownFilterBarItem
                filterItemKey="status"
                placeholder={Resources.Status}
                selection={this.selectionRequestedTab}
                items={requestsfilterMenuItems}
                hideClearAction={true}
                filter={this.requestedFilter}
            />
        );
    };

    private showSercurityDialog = (): void => {
        this.isSecurityDialogOpen.value = true;
    };

    private switchToMasterDetailView = (selectedExtension: IExtensionViewModel, allExtensions: IExtensionViewModel[]): void => {
        this.createNewEntryOnNextUpdate = true;
        this.selectedExtension = selectedExtension;
        this.currentExtensions = allExtensions;
        this.selectedExtensionIdentifier.value = this.extensionsService.getExtensionIdentifier(selectedExtension);
    };

    private switchToMainView = (): void => {
        this.createNewEntryOnNextUpdate = true;
        this.selectedExtensionIdentifier.value = "";
    };

    private updateBreadCrumb = (value: string, action: string): void => {
        this.extensionManagementBreadcrumbService.setSelectedTabBreadcrumb(tabIdToTabName[value]);
    };
}

VssComponent.register("extension-management-hub", ExtensionsManagementHub);
